import { msalInstance } from "../auth/authService";
import { AuthError, InteractionRequiredAuthError } from "@azure/msal-browser";

// Azure best practice: Define scopes in a dedicated constant with proper comments
const SPE_SCOPES = [
  "openid", 
  "profile", 
  "offline_access",
  "User.Read", 
  "Files.ReadWrite.All", 
  "Sites.FullControl.All",  // Required for SharePoint Embedded container management
  "FileStorageContainer.Selected"
];

/**
 * Authentication provider for SharePoint Embedded services
 * @azure Azure best practice: Use interfaces for auth providers to allow mock implementations for testing
 */
export interface ISPEAuthProvider {
  /**
   * Gets an access token for SharePoint Embedded services
   * @returns A promise that resolves to the access token
   */
  getAccessToken(): Promise<string>;
}

/**
 * Implementation of the authentication provider for SharePoint Embedded
 * @azure Azure best practice: Implement token caching to avoid unnecessary token fetches
 */
export class SPEAuthProvider implements ISPEAuthProvider {
  private cachedToken: string | null = null;
  private tokenExpiry: Date | null = null;
  
  /**
   * Gets an access token for SharePoint Embedded services
   * Uses cached token if available and not expired
   * @returns A promise that resolves to the access token
   */
  public async getAccessToken(): Promise<string> {
    // Check if cached token is still valid (with 5 min buffer)
    if (this.cachedToken && this.tokenExpiry && 
        this.tokenExpiry > new Date(Date.now() + 5 * 60 * 1000)) {
      return this.cachedToken;
    }
    
    try {
      // Azure best practice: Use MSAL for token acquisition with proper error handling
      const accounts = msalInstance.getAllAccounts();
      
      if (accounts.length === 0) {
        throw new Error("No authenticated accounts found");
      }
      
      // Force token refresh by setting forceRefresh to true
      const result = await msalInstance.acquireTokenSilent({
        scopes: SPE_SCOPES,
        account: accounts[0],
        forceRefresh: true  // Force refresh to get new token with updated scopes
      });
      
      // Azure best practice: Ensure non-null value is returned
      if (!result.accessToken) {
        throw new Error("No access token returned from authentication provider");
      }
      
      this.cachedToken = result.accessToken;
      
      // Set token expiry based on MSAL expiration
      const expiresOn = result.expiresOn || new Date(Date.now() + 3600 * 1000);
      this.tokenExpiry = expiresOn;
      
      return this.cachedToken;
    } catch (error: unknown) {
      // Azure best practice: Discriminate error types for proper handling
      if (error instanceof InteractionRequiredAuthError) {
        try {
          // Fall back to interactive authentication if silent auth fails
          const result = await msalInstance.acquireTokenPopup({
            scopes: SPE_SCOPES
          });
          
          if (!result.accessToken) {
            throw new Error("No access token returned after interactive authentication");
          }
          
          this.cachedToken = result.accessToken;
          const expiresOn = result.expiresOn || new Date(Date.now() + 3600 * 1000);
          this.tokenExpiry = expiresOn;
          
          return this.cachedToken;
        } catch (interactiveError: unknown) {
          console.error("Interactive authentication failed:", interactiveError);
          throw new Error(`Interactive authentication failed: ${interactiveError instanceof Error ? interactiveError.message : String(interactiveError)}`);
        }
      }
      
      // Azure best practice: Log detailed error information
      console.error("Error fetching SPE access token:", error);
      
      // Azure best practice: Provide structured error messages
      if (error instanceof AuthError) {
        throw new Error(`Authentication failed: ${error.errorCode} - ${error.errorMessage}`);
      } else if (error instanceof Error) {
        throw new Error(`Authentication failed: ${error.message}`);
      } else {
        throw new Error(`Authentication failed: ${String(error)}`);
      }
    }
  }

  /**
   * Clears the cached token to force refresh on next request
   * Useful when permissions have been updated
   */
  public clearTokenCache(): void {
    this.cachedToken = null;
    this.tokenExpiry = null;
  }
}

export default SPEAuthProvider;
